import { AssetGroup } from '@play-co/timestep-core';
import PopupBasicLazy from './PopupBasicLazy';
import View from '@play-co/timestep-core/lib/ui/View';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import sounds from 'src/lib/sounds';

export default class PopupShadowBox extends PopupBasicLazy {
  private imageUrl!: string;
  private image: ImageScaleView;
  private padding!: number;

  constructor(opts: { superview: View; close: VoidFunction }) {
    super({
      ...opts,
      skipTitle: true,
      skipMessage: true,
      tracking: {
        feature: 'popup',
        $subFeature: 'popup-shadowbox',
      },
      zIndex: 10000,
    });

    this.box.removeFromSuperview();
    this.box = this.createShadowBox(this.root);
    this.image = this.createBackgroundImage(this.box);

    this.box.addSubview(this.buttonClose);
    this.buttonClose.updateOpts({
      width: 90,
      height: 90,
    });
    this.buttonClose.onClick = async () => {
      sounds.sfx.play('cancel');
      return opts.close();
    };
  }

  /**
   * Create main box
   */
  private createShadowBox(superview: View) {
    const { width, height } = superview.style;
    return new View({
      superview,
      backgroundColor: 'rgba(0,0,0,0.6)',
      infinite: true,
      canHandleEvents: false,
      x: width * 0.5,
      y: height * 0.5,
    });
  }

  /**
   * Create main background view of popup
   */
  private createBackgroundImage(superview: View) {
    return new ImageScaleView({
      superview,
      infinite: true,
      canHandleEvents: false,
      autoSize: true,
      scaleMethod: 'contain',
      verticalAlign: 'center',
      horizontalAlign: 'center',
    });
  }

  public init(opts: { image: string; padding?: number }) {
    this.imageUrl = opts.image;
    this.padding = opts.padding ?? 20;

    // Start loading shadowbox asset
    super.init({});
  }

  protected getAssetsGroup() {
    return new AssetGroup([this.imageUrl]);
  }

  protected async loadAssets() {
    await super.loadAssets();

    const { padding } = this;
    const sizeDelta = 2 * padding;

    // Calculate popup dimensions
    this.image.setImage(this.imageUrl);
    let { width, height } = this.image.style;
    let boxWidth = width + sizeDelta;
    let boxHeight = height + sizeDelta;

    // Scale down to fit screen if necessary
    const maxWidth =
      this.root.style.width - this.buttonClose.style.width + padding;
    const maxHeight =
      this.root.style.height - this.buttonClose.style.height + padding;
    if (boxWidth > maxWidth) {
      boxWidth = maxWidth;
      boxHeight = (boxWidth * (height + sizeDelta)) / (width + sizeDelta);
    }
    if (boxHeight > maxHeight) {
      boxHeight = maxHeight;
      boxWidth = (boxHeight * (width + sizeDelta)) / (height + sizeDelta);
    }

    // Scale and position views
    this.box.updateOpts({
      width: boxWidth,
      height: boxHeight,
      centerOnOrigin: true,
    });
    this.image.updateOpts({
      x: padding,
      y: padding,
      width: boxWidth - sizeDelta,
      height: boxHeight - sizeDelta,
    });
    this.buttonClose.updateOpts({
      centerOnOrigin: true,
      x: boxWidth - padding,
      y: padding,
    });
  }
}
